/**
 * RiskOverlayStrip — top-of-page risk indicators for the Participant
 * Detail page. Server (`GET /api/careplans/{plan_id}/risk-overlay`)
 * already enforces permission-gating; this component only renders.
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ShieldAlert, AlertTriangle, Activity, Phone, Clock } from 'lucide-react';

const ICON = {
  allergies: AlertTriangle,
  medical_alerts: AlertTriangle,
  falls_risk: Activity,
  behaviour_support: ShieldAlert,
  restrictive_practice: ShieldAlert,
  emergency: Phone,
  review_overdue: Clock,
};

const TONE = {
  high: 'bg-rose-50 text-rose-800 border-rose-200',
  medium: 'bg-amber-50 text-amber-800 border-amber-200',
  low: 'bg-zinc-50 text-zinc-700 border-zinc-200',
};

export function RiskOverlayStrip({ planId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!planId) return;
    api.get(`/careplans/${planId}/risk-overlay`).then(r => setData(r.data.data)).catch(() => setData(null));
  }, [planId]);
  if (!data?.indicators?.length) return null;
  return (
    <Card className="border-rose-200" data-testid="risk-overlay-strip">
      <CardContent className="p-2 flex flex-wrap gap-2 items-center">
        <span className="text-xs font-semibold text-rose-800 uppercase tracking-wide mr-1">Risk Overlay:</span>
        {data.indicators.map((ind, i) => {
          const Icon = ICON[ind.kind] || ShieldAlert;
          return (
            <Badge key={i} variant="outline"
                   className={`${TONE[ind.severity] || TONE.medium} text-[11px] gap-1`}
                   data-testid={`risk-indicator-${ind.kind}`}>
              <Icon className="h-3 w-3" />
              {ind.label}
              {ind.visible === false && (
                <span className="opacity-60 ml-1">(gated)</span>
              )}
            </Badge>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default RiskOverlayStrip;
